import * as React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { signInWithOAuth } from '../../lib/oauth';
import { findQrSession, markQrSessionUsed } from '../../db/qrSessionRepository';
import { findPdfsByUser } from '../../db/pdfRepository';
import LoginForm from '../../components/forms/LoginForm';
import SocialLoginButtons from '../../components/auth/SocialLoginButtons';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  // 모바일 감지
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 저장된 로그인 정보 불러오기
  useEffect(() => {
    const savedEmail = localStorage.getItem('saved_email');
    const savedPassword = localStorage.getItem('saved_password');
    const savedRememberMe = localStorage.getItem('remember_me') === 'true';

    if (savedEmail && savedPassword && savedRememberMe) {
      setEmail(savedEmail);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.user) {
        // 로그인 상태 유지 처리
        if (rememberMe) {
          localStorage.setItem('saved_email', email);
          localStorage.setItem('saved_password', password);
          localStorage.setItem('remember_me', 'true');
        } else {
          localStorage.removeItem('saved_email');
          localStorage.removeItem('saved_password');
          localStorage.removeItem('remember_me');
        }

        // 대기 중인 QR 토큰이 있는지 확인 (QR 스캔 후 로그인한 경우)
        const pendingToken = localStorage.getItem('pending_qr_token');
        if (pendingToken) {
          localStorage.removeItem('pending_qr_token');

          // QR 세션 유효성 확인 후 연결
          const session = await findQrSession(pendingToken);

          if (session && !session.is_used && new Date(session.expires_at) > new Date()) {
            await markQrSessionUsed(pendingToken, data.user.id);

            localStorage.setItem('qr_auto_play', 'true');
            localStorage.setItem('qr_session_token', pendingToken);
            localStorage.setItem('qr_user_id', data.user.id);
            navigate('/dashboard?mode=remote');
            return;
          }
        }

        // 사용자의 PDF 자동 재생 설정 확인
        const pdfs = await findPdfsByUser(data.user.id);
        const autoPlayPdf = pdfs.find((p) => p.auto_fullscreen);

        if (autoPlayPdf && isMobile) {
          navigate(`/presentations/${autoPlayPdf.id}/remote`);
          return;
        }

        navigate('/dashboard');
      }
    } catch (err: any) {
      setError(err.message || t('auth.login_failed'));
    } finally {
      setLoading(false);
    }
  };

  // 소셜 로그인
  const handleSocialLogin = async (provider: 'google' | 'kakao') => {
    try {
      await signInWithOAuth(provider);
    } catch (err: any) {
      setError(err.message || t('home.social_login_failed'));
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* 로고 */}
        <div className="flex justify-center mb-8">
          <Link to="/">
            <img
              src="https://static.readdy.ai/image/fa9e51599f3a91cf824c0f1e50ffec3c/bbe0145b0a3abb3ea6b08059cd830213.png"
              alt="Qpresen Logo"
              className="h-20 object-contain hover:opacity-80 transition-opacity"
            />
          </Link>
        </div>

        {/* 로그인 폼 */}
        <LoginForm
          email={email}
          password={password}
          rememberMe={rememberMe}
          loading={loading}
          error={error || undefined}
          onChangeEmail={setEmail}
          onChangePassword={setPassword}
          onChangeRememberMe={setRememberMe}
          onSubmit={handleLogin}
        />

        {/* 링크들 */}
        <div className="flex items-center justify-center gap-4 mt-4 text-sm">
          <button className="text-gray-500 hover:text-gray-700 cursor-pointer whitespace-nowrap">
            {t('home.find_id')}
          </button>
          <span className="text-gray-300">|</span>
          <button className="text-gray-500 hover:text-gray-700 cursor-pointer whitespace-nowrap">
            {t('home.find_password')}
          </button>
          <span className="text-gray-300">|</span>
          <Link to="/signup" className="text-gray-500 hover:text-gray-700 whitespace-nowrap">
            {t('home.signup_with_email')}
          </Link>
        </div>

        {/* 구분선 */}
        <div className="flex items-center my-6">
          <div className="flex-1 border-t border-gray-200"></div>
          <span className="px-4 text-xs text-gray-400 whitespace-nowrap">{t('home.sns_login_divider')}</span>
          <div className="flex-1 border-t border-gray-200"></div>
        </div>

        {/* 소셜 로그인 버튼들 */}
        <SocialLoginButtons onSocialLogin={handleSocialLogin} />

        {/* 뒤로가기 버튼 */}
        <Link
          to="/"
          className="w-full mt-6 py-2.5 text-sm text-gray-500 hover:text-gray-700 transition-colors block text-center"
        >
          {t('home.go_back')}
        </Link>
      </div>
    </div>
  );
}
